import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CellSub,
  CodeChip,
  EmptyState,
  KeyValue,
  PageHeader,
  SearchInput,
  Skeleton,
  Switch,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@provenance/design';
import { RegistrationStatus, UserRole, formatAbn, type RegistrationSummary } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CheckCircle2,
  FileClock,
  Mail,
  MessageSquareWarning,
  Paperclip,
  Phone,
  UserPlus,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ApproveRegistrationDialog,
  RejectRegistrationDialog,
  RequestInformationDialog,
} from '../features/registrations/RegistrationDialogs';
import { LodgeRegistrationDialog } from '../features/registrations/LodgeRegistrationDialog';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { auDate, auDateTime, humanise, relative } from '../lib/format';
import { RegistrationStatusBadge } from '../lib/status';

/**
 * Registration requests (spec §7.3 / §10.2).
 *
 * This screen closes what was, until now, a broken promise: the public form accepted
 * applications and nothing in the product could act on them. Someone who registered got
 * silence.
 *
 * The three actions are deliberately equal in weight. A chamber's instinct with an
 * imperfect application is to reject it, and rejection means the applicant starts again
 * from an empty form — so "Request information" sits alongside the other two rather than
 * behind a menu.
 */

const ACTIONABLE = [
  RegistrationStatus.SUBMITTED,
  RegistrationStatus.INFO_REQUESTED,
  RegistrationStatus.INFO_PROVIDED,
] as string[];

/**
 * Whether Provenir Support may approve or reject here (T-147). Support lodges and asks for more
 * freely; deciding is the chamber's unless it has given standing consent.
 */
function useSupportMayDecide() {
  return useQuery({
    queryKey: ['registration-settings'],
    queryFn: () => api.get<{ supportMayDecide: boolean }>('/api/console/registration-settings'),
  });
}

/** The chamber's standing consent, on the Registrations page where it applies. Admins only. */
function SupportConsent() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useSupportMayDecide();
  const save = useMutation({
    mutationFn: (supportMayDecide: boolean) => api.post('/api/console/registration-settings', { supportMayDecide }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['registration-settings'] }),
    onError: (error) => toast.error('Could not save', error instanceof ApiError ? error.message : ''),
  });
  return (
    <Card className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="[font-size:13.5px] font-medium">Provenir Support may approve or reject registrations</p>
        <p className="[font-size:12.5px] text-[var(--pv-muted)]">
          Off: Support can lodge an application for an exporter and ask for more, and your team decides. Recorded either way.
        </p>
      </div>
      <Switch
        checked={settings.data?.supportMayDecide ?? false}
        disabled={settings.isLoading || save.isPending}
        onCheckedChange={(v) => save.mutate(v)}
        aria-label="Provenir Support may approve or reject registrations"
      />
    </Card>
  );
}

export function Registrations() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [lodging, setLodging] = useState(false);
  const [search, setSearch] = useState('');
  const [showDecided, setShowDecided] = useState(false);

  const query = useQuery({
    queryKey: ['registrations'],
    queryFn: () => api.get<RegistrationSummary[]>('/api/console/registrations'),
  });

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const term = search.trim().toLowerCase();
    return all
      .filter((r) => (showDecided ? true : ACTIONABLE.includes(r.status)))
      .filter(
        (r) =>
          !term ||
          r.companyName.toLowerCase().includes(term) ||
          r.contactEmail.toLowerCase().includes(term) ||
          r.abn.includes(term.replace(/\s/g, '')),
      );
  }, [query.data, search, showDecided]);

  const waiting = (query.data ?? []).filter((r) => ACTIONABLE.includes(r.status)).length;
  // Applications where the applicant has already replied jump the queue: someone is
  // waiting on the chamber, and they have already done their part.
  const responded = (query.data ?? []).filter((r) => r.status === RegistrationStatus.INFO_PROVIDED);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Registrations"
        lede="Exporters and freight forwarders who applied through the public registration form. Approving one creates their organisation and invites their first user."
        actions={
          <Button variant="secondary" leadingIcon={<UserPlus className="size-3.5" />} onClick={() => setLodging(true)}>
            Register an exporter
          </Button>
        }
      />
      <LodgeRegistrationDialog open={lodging} onOpenChange={setLodging} />
      {user?.role === UserRole.CHAMBER_ADMIN && <SupportConsent />}

      {responded.length > 0 && (
        <Callout
          tone="info"
          icon={<MessageSquareWarning className="size-4" />}
          title={`${responded.length} applicant${responded.length === 1 ? ' has' : 's have'} responded`}
        >
          They have supplied what you asked for and are waiting on you.
        </Callout>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Company, contact email or ABN"
          className="w-full sm:w-[22rem]"
          aria-label="Search registrations"
        />
        <Button
          variant={showDecided ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setShowDecided((v) => !v)}
        >
          {showDecided ? 'Showing all' : 'Show decided too'}
        </Button>
        <div className="flex-1" />
        <span className="[font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
          {waiting} awaiting your decision
        </span>
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          tone={waiting === 0 ? 'good' : 'neutral'}
          icon={waiting === 0 ? <CheckCircle2 className="size-5" /> : <UserPlus className="size-5" />}
          title={search ? 'Nothing matches that search' : 'No applications waiting'}
          description={
            search
              ? 'Try a different company name, email or ABN.'
              : 'Companies that apply through your public registration page appear here, and you are emailed when they do.'
          }
        />
      ) : (
        <TableShell footer={<span>{rows.length} shown</span>}>
          <Table className="min-w-[860px]">
            <Thead>
              <Tr>
                <Th>Company</Th>
                <Th>ABN</Th>
                <Th>Type</Th>
                <Th>Contact</Th>
                <Th>Applied</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id} onClick={() => navigate(`/registrations/${row.id}`)}>
                  <Td>
                    <span className="font-medium">{row.companyName}</span>
                  </Td>
                  <Td>
                    <span className="pv-mono [font-size:12.5px]">{formatAbn(row.abn)}</span>
                  </Td>
                  <Td>
                    <Badge tone="neutral" size="sm">
                      {row.applicantType === 'EXPORTER' ? 'Exporter' : 'Freight forwarder'}
                    </Badge>
                  </Td>
                  <Td>
                    {row.contactName}
                    <CellSub>{row.contactEmail}</CellSub>
                  </Td>
                  <Td>
                    {auDate(row.submittedAt)}
                    <CellSub>{relative(row.submittedAt)}</CellSub>
                  </Td>
                  <Td>
                    <RegistrationStatusBadge status={row.status} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}
    </div>
  );
}

interface RegistrationDetailData {
  id: string;
  companyName: string;
  tradingName: string | null;
  abn: string;
  applicantType: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
  addressLine1: string;
  addressLine2: string | null;
  suburb: string;
  state: string;
  postcode: string;
  status: string;
  infoRequestedText: string | null;
  infoRequestedAt: string | null;
  infoRespondBy: string | null;
  infoProvidedAt: string | null;
  infoProvidedMessage: string | null;
  infoRequestedItems: Array<{ code: string; label: string }>;
  rejectionFreeText: string | null;
  rejectionReason: { code: string; label: string } | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdOrganisationId: string | null;
  createdAt: string;
  submittedIp: string | null;
  attachments: Array<{ id: string; filename: string; origin: string; sizeBytes: number; createdAt: string }>;
}

export function RegistrationDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useSession();
  const consent = useSupportMayDecide();
  const mayDecide = user?.role !== UserRole.PLATFORM_SUPPORT || consent.data?.supportMayDecide === true;
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const query = useQuery({
    queryKey: ['registration', id],
    queryFn: () => api.get<RegistrationDetailData>(`/api/console/registrations/${id}`),
    enabled: Boolean(id),
  });

  if (query.isLoading) return <Skeleton className="h-96" />;
  if (!query.data) {
    return (
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Not found">
        {query.error instanceof ApiError ? query.error.message : 'That registration is not available.'}
        <div className="mt-3">
          <Button variant="secondary" size="sm" asChild>
            <Link to="/registrations">Back to registrations</Link>
          </Button>
        </div>
      </Callout>
    );
  }

  const r = query.data;
  const actionable = ACTIONABLE.includes(r.status);
  const contactName = `${r.contactFirstName} ${r.contactLastName}`;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/registrations"
          className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
        >
          <ArrowLeft aria-hidden className="size-3.5" /> All registrations
        </Link>
        <div className="mt-2 flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="[font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em]">
                {r.companyName}
              </h1>
              <RegistrationStatusBadge status={r.status} />
            </div>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 [font-size:13px] text-[var(--pv-muted)]">
              <span className="pv-mono">ABN {formatAbn(r.abn)}</span>
              <span aria-hidden>·</span>
              <span>{r.applicantType === 'EXPORTER' ? 'Exporter' : 'Freight forwarder'}</span>
              <span aria-hidden>·</span>
              <span>applied {relative(r.createdAt)}</span>
            </p>
          </div>

          {actionable && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                leadingIcon={<MessageSquareWarning className="size-3.5" />}
                onClick={() => setInfoOpen(true)}
              >
                Request information
              </Button>
              {mayDecide ? (
                <>
                  <Button variant="secondary" leadingIcon={<X className="size-3.5" />} onClick={() => setRejectOpen(true)}>
                    Reject
                  </Button>
                  <Button
                    variant="primary"
                    leadingIcon={<CheckCircle2 className="size-3.5" />}
                    onClick={() => setApproveOpen(true)}
                  >
                    Approve
                  </Button>
                </>
              ) : (
                <span className="[font-size:12.5px] text-[var(--pv-muted)]">The chamber decides its registrations.</span>
              )}
            </div>
          )}
        </div>
      </div>

      {r.status === RegistrationStatus.INFO_REQUESTED && (
        <Callout
          tone="warn"
          icon={<FileClock className="size-4" />}
          title={`Waiting on ${r.companyName} since ${auDate(r.infoRequestedAt)}`}
        >
          {r.infoRequestedItems.length > 0 && (
            <ul className="m-0 list-disc pl-4">
              {r.infoRequestedItems.map((item) => (
                <li key={item.code}>{item.label}</li>
              ))}
            </ul>
          )}
          {r.infoRequestedText && <p className="mt-1.5">{r.infoRequestedText}</p>}
          {r.infoRespondBy && (
            <p className="mt-2 [font-size:var(--pv-text-xs)]">
              Asked to respond by {auDate(r.infoRespondBy)}.
            </p>
          )}
        </Callout>
      )}

      {r.status === RegistrationStatus.INFO_PROVIDED && (
        <Callout
          tone="info"
          icon={<MessageSquareWarning className="size-4" />}
          title={`${r.companyName} responded ${relative(r.infoProvidedAt)}`}
        >
          {r.infoProvidedMessage ?? 'They supplied what was asked for. Their attachments are below.'}
        </Callout>
      )}

      {r.status === RegistrationStatus.APPROVED && (
        <Callout tone="ok" icon={<CheckCircle2 className="size-4" />} title="Approved">
          The organisation and its first user were created on {auDateTime(r.decidedAt)}.
          {r.decisionNote && <p className="mt-1.5">{r.decisionNote}</p>}
        </Callout>
      )}

      {r.status === RegistrationStatus.REJECTED && (
        <Callout
          tone="stop"
          icon={<X className="size-4" />}
          title={`Rejected — ${r.rejectionReason?.label ?? 'Other'}`}
        >
          {r.rejectionFreeText}
          <p className="mt-2 [font-size:var(--pv-text-xs)]">{auDateTime(r.decidedAt)}</p>
        </Callout>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="The company" hint="As supplied" />
          <dl className="m-0">
            <KeyValue label="Registered name">{r.companyName}</KeyValue>
            {r.tradingName && <KeyValue label="Trading as">{r.tradingName}</KeyValue>}
            <KeyValue label="ABN" mono>
              {formatAbn(r.abn)}
            </KeyValue>
            <KeyValue label="Type">
              {r.applicantType === 'EXPORTER' ? 'Exporter' : 'Freight forwarder'}
            </KeyValue>
            <KeyValue label="Address">
              <span className="block text-right">
                {[r.addressLine1, r.addressLine2].filter(Boolean).join(', ')}
                <br />
                {r.suburb} {r.state} {r.postcode}
              </span>
            </KeyValue>
          </dl>
        </Card>

        <Card>
          <CardHeader title="Primary contact" hint="Becomes the first user" />
          <dl className="m-0">
            <KeyValue label="Name">{contactName}</KeyValue>
            <KeyValue label="Email">
              <a href={`mailto:${r.contactEmail}`} className="inline-flex items-center gap-1.5 underline">
                <Mail aria-hidden className="size-3" />
                {r.contactEmail}
              </a>
            </KeyValue>
            <KeyValue label="Phone">
              <a href={`tel:${r.contactPhone}`} className="inline-flex items-center gap-1.5">
                <Phone aria-hidden className="size-3" />
                {r.contactPhone}
              </a>
            </KeyValue>
            <KeyValue label="Applied">{auDateTime(r.createdAt)}</KeyValue>
            {r.submittedIp && (
              <KeyValue label="From" mono>
                {r.submittedIp}
              </KeyValue>
            )}
          </dl>
        </Card>
      </div>

      {r.attachments.length > 0 && (
        <Card padded={false}>
          <div className="px-5 pt-5">
            <CardHeader
              title="Attachments"
              hint={`${r.attachments.length}`}
              description="Supplied by the applicant, either with the application or in response to a request."
            />
          </div>
          <ul className="flex list-none flex-col gap-0 border-t border-[var(--pv-line)] p-0">
            {r.attachments.map((attachment) => (
              <li
                key={attachment.id}
                className="flex flex-wrap items-center gap-3 border-b border-[var(--pv-line)] px-5 py-3.5 last:border-b-0"
              >
                <Paperclip aria-hidden className="size-4 shrink-0 text-[var(--pv-faint)]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate [font-size:13.5px] font-medium">{attachment.filename}</p>
                  <p className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                    {humanise(attachment.origin)} · {relative(attachment.createdAt)}
                  </p>
                </div>
                <CodeChip>{Math.round(attachment.sizeBytes / 1024)} KB</CodeChip>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {r.createdOrganisationId && (
        <Card className="flex items-center gap-3">
          <Building2 aria-hidden className="size-5 shrink-0 text-[var(--pv-ok)]" />
          <p className="min-w-0 flex-1 [font-size:13.5px]">
            This application created an organisation in your chamber. Its certificate requests
            appear in your queues like any other.
          </p>
        </Card>
      )}

      {actionable && (
        <>
          <ApproveRegistrationDialog
            registration={{ id: r.id, companyName: r.companyName, contactEmail: r.contactEmail, contactName }}
            open={approveOpen}
            onOpenChange={setApproveOpen}
          />
          <RejectRegistrationDialog
            registration={{ id: r.id, companyName: r.companyName }}
            open={rejectOpen}
            onOpenChange={setRejectOpen}
          />
          <RequestInformationDialog
            registration={{ id: r.id, companyName: r.companyName }}
            open={infoOpen}
            onOpenChange={setInfoOpen}
          />
        </>
      )}
    </div>
  );
}
